import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { DATE_FORMATS } from '@dealstack/shared/lib/format/dates/date';
import { Button, ButtonProps, Flex, Menu, rem } from '@mantine/core';
import { DatePicker, DatePickerProps } from '@mantine/dates';
import { endOfDay, format } from 'date-fns';
import { CSSProperties, useState } from 'react';
import {
	PredefinedIconSize,
	PredefinedIcons,
} from '@dealstack/frontend/shared/components/fundament/Icon/Icon.types';

interface Props extends DatePickerProps {
	dateTo: Date | null;
	setFilterDate: (date: Date | null) => void;
	targetIcon?: PredefinedIcons;
	targetIconSize?: PredefinedIconSize;
	targetFlexDirection?: CSSProperties['flexDirection'];
	targetButtonProps?: ButtonProps;
	buttonDefaultLabel?: string;
}

export const DateSelectionMenu = ({
	dateTo,
	setFilterDate,
	targetIcon = 'calendar-outlined',
	targetFlexDirection = 'row',
	targetIconSize = 'lg',
	buttonDefaultLabel,
	targetButtonProps,
	...datePickerProps
}: Props) => {
	const tCapManage = useTranslationCapManage();
	const [menuOpened, setMenuOpened] = useState(false);
	const handleDateChange = (date: Date | null) => {
		if (date) {
			const endOfDayDate = endOfDay(date);
			setFilterDate(endOfDayDate);
		} else {
			setFilterDate(null);
		}
		setMenuOpened(false);
	};

	const buttonDate = dateTo
		? format(dateTo, DATE_FORMATS.CALENDAR_DATE)
		: buttonDefaultLabel ?? tCapManage('header_actions.buttons.today');

	return (
		<div style={{ position: 'relative' }}>
			<Menu
				position="bottom-start"
				opened={menuOpened}
				onClose={() => setMenuOpened(false)}
				onOpen={() => setMenuOpened(true)}
				withinPortal
			>
				<Menu.Target>
					<Button variant="default" {...targetButtonProps}>
						<Flex direction={targetFlexDirection} align="center" gap="0.5rem">
							<Icon variant={targetIcon} size={targetIconSize} />
							{buttonDate}
						</Flex>
					</Button>
				</Menu.Target>
				<Menu.Dropdown sx={{ padding: 0 }}>
					<div style={{ padding: `${rem('20px')} ${rem('24px')}` }}>
						<DatePicker
							value={dateTo}
							nextIcon={<Icon variant="caret-right" size="lg" />}
							previousIcon={<Icon variant="caret-left" size="lg" />}
							onChange={handleDateChange}
							{...datePickerProps}
						/>
					</div>
					<Menu.Divider m={0} />
					<div style={{ padding: `${rem('12px')} ${rem('16px')}` }}>
						<Button
							fullWidth
							variant="ghost"
							onClick={() => handleDateChange(new Date())}
							sx={{ fontWeight: 400 }}
						>
							{tCapManage('header_actions.buttons.all_holdings')}
						</Button>
					</div>
				</Menu.Dropdown>
			</Menu>
		</div>
	);
};
